import React, { useState, useContext, useMemo, useEffect } from 'react'

import { InternshipStates } from '../../../utils/constants'
import { Popconfirm, Button, Dropdown, Menu, Badge } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import { faEllipsisH, faTrash, faUserGraduate, faCalendar, faComment, faCopy } from '@fortawesome/pro-solid-svg-icons'
import { GlobalContext } from '../../../Providers/GlobalProvider'
import { isSchoolMaccsUser } from '../../../utils/roles'
import { ColorsPalette } from '../../../config/colors'
import { isFunction } from 'lodash'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'

import '../../../assets/shared/student-badge-quick-menu.css'
import { MaccsInternshipMessageContext } from '../../../Providers/MaccsinternshipMessageProvider'

const { PENDING, ACCEPTED, CANCELED, PRE_ACCEPTED } = InternshipStates
/** @enum */
const MessagesState = Object.freeze({
  NO_MESSAGES: 0,
  HAS_MESSAGES: 1,
  HAS_NEW_MESSAGES: 2
})

const AssistantMenu = ({ internship, noMargin, onSelectedAssistant, onSelectedInternship, onDuplicateInternship, onInternshipDelete }) => {
  const { user } = useContext(GlobalContext)
  const { setSelectedInternship, messagesCount } = useContext(MaccsInternshipMessageContext)

  const t = useSelector(selectTranslate)

  const [removePopConfirmDisplayed, setIsDeletePopConfirmDisplayed] = useState(false)
  const [messagesState, setMessagesStates] = useState(MessagesState.NO_MESSAGES)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (internship) {
      const messages = messagesCount[internship.id] ?? {}

      if (messages.unreadMessages > 0) {
        setMessagesStates(MessagesState.HAS_NEW_MESSAGES)
      } else if (messages.messages > 0) {
        setMessagesStates(MessagesState.HAS_MESSAGES)
      } else {
        setMessagesStates(MessagesState.NO_MESSAGES)
      }
    }
  }, [internship, messagesCount])

  const canDeleteInternship = useMemo(() => {
    let isGranted = isFunction(onInternshipDelete)

    if (isSchoolMaccsUser(user)) {
      isGranted = isGranted && internship.state !== CANCELED && internship.state !== ACCEPTED
    } else {
      isGranted = false
    }

    return isGranted
  }, [user, onInternshipDelete, internship])

  const handleDeleteCancel = e => {
    setIsDeletePopConfirmDisplayed(false)

    e.preventDefault()
    e.stopPropagation()
  }

  const handleDeleteClick = e => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleConfirm = () => {
    setIsDeletePopConfirmDisplayed(false)
    onInternshipDelete(internship)
  }

  const renderMessageNotificationDot = () => {
    return <Badge color={getNotificationColorByInternship(internship)} />
  }

  const getNotificationColorByInternship = () => {
    if (messagesState === MessagesState.HAS_MESSAGES) {
      return ColorsPalette.SECOND
    }

    if (messagesState === MessagesState.HAS_NEW_MESSAGES) {
      return ColorsPalette.RED
    }

    return ColorsPalette.TRANSPARENT
  }

  const renderInternshipMenu = internship => {
    return (
      <Menu>
        <div className='student-badge-internship-id bold' style={{ marginLeft: '16px' }}>
          {t('common.id')}: {internship.id}
        </div>
        <Menu.Item onClick={() => setSelectedInternship(internship)}>
          {renderMessageNotificationDot(internship)}
          <FontAwesomeIcon icon={faComment} />
          {t('student_quick_menu.internship_menu.discussion')}
        </Menu.Item>
        {internship.assistant && (
          <Menu.Item onClick={() => onSelectedAssistant(internship.assistant)}>
            <FontAwesomeIcon icon={faUserGraduate} />
            {t('Assistant sheet')}
          </Menu.Item>
        )}
        {!(internship.state < PENDING && internship.state !== PRE_ACCEPTED) && (
          <Menu.Item onClick={() => onSelectedInternship(internship)}>
            <FontAwesomeIcon icon={faCalendar} />
            {t('Internship sheet')}
          </Menu.Item>
        )}
        {isSchoolMaccsUser(user) && isFunction(onDuplicateInternship) && (
          <Menu.Item onClick={() => onDuplicateInternship(internship)}>
            <FontAwesomeIcon icon={faCopy} />
            {t('Duplicate')}
          </Menu.Item>
        )}
        {canDeleteInternship && (
          <Menu.Item style={{ padding: '0px' }} onClick={(e) => { e.domEvent.preventDefault(); return false }}>
            <Popconfirm
              placement='right'
              okType='danger'
              title={t('Delete this internship ?')}
              okText={t('Yes')}
              cancelText={t('Cancel')}
              visible={removePopConfirmDisplayed}
              onConfirm={handleConfirm}
              onCancel={handleDeleteCancel}
              onClick={handleDeleteClick}
            >
              <div style={{ padding: '5px 12px' }} onClick={() => setIsDeletePopConfirmDisplayed(true)}>
                <FontAwesomeIcon icon={faTrash} style={{ cursor: 'pointer' }} />
                {t('Delete')}
              </div>
            </Popconfirm>
          </Menu.Item>
        )}
      </Menu>
    )
  }

  return (
    <div>
      {internship && (
        <span
          className={`student-badge-quick-menu ${noMargin ? 'no-margin' : ''}`}
        >
          {renderMessageNotificationDot(internship)}
          <Dropdown
            visible={visible}
            overlay={renderInternshipMenu(internship)}
            trigger={['click']}
            onVisibleChange={(visible) => {
              if (!visible) {
                setIsDeletePopConfirmDisplayed(false)
              }

              setVisible(visible)
            }}
          >
            <Button size='small' type='primary' shape='circle' onClick={() => setVisible(!visible)}>
              <FontAwesomeIcon icon={faEllipsisH} />
            </Button>
          </Dropdown>
        </span>
      )}
    </div>
  )
}

export default AssistantMenu
